'use client'

import { Button } from '@lastbrand/web-ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
  useCarousel,
} from '@lastbrand/web-ui/carousel'
import { StarRatingBar } from '@lastbrand/web-ui/star-rating-bar'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef, type HTMLAttributes, type ReactNode } from 'react'

import { DateTime, STANDARD_DATE_OVERRIDES } from '@src/blocks/date-time'
import { ModalDS } from '@src/components/blocks/modal-ds'
import { Image } from '@src/quince-components/src/atoms/image'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import type ProductReview from '@src/quince-site-common/src/domains/ProductReview'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { getReviewImageAltText } from '@src/quince-site-pdp/src/atoms/reviewPopUp/utils'
import { toStarRatingValue } from '@src/utils/star-rating.utils'

// Presentational core shared by the non-PL and PL DS photo popups; state wiring lives in the wrappers.

interface SlideReview {
  imageUrl: string
  author: string
  rating: number
  createdAt: string
  title: string
  body: string
}

export interface ReviewPopupViewDSProps {
  showPopUp: boolean
  productReviews: ProductReview[]
  reviewId: number
  imageIndex: number
  onClose: () => void
  testId: string
  // Pagination: onNearEnd fires once per approach to the last loaded slide while hasMore is true.
  hasMore?: boolean
  onNearEnd?: () => void
}

const PANEL_WIDTH_LG = '1000px'
const PANEL_HEIGHT_LG = '600px'
const IMAGE_WIDTH_LG = '472px'

// Trigger the next page once the selection is within this many slides of the last loaded one.
const NEAR_END_THRESHOLD = 3

// Mobile offsets measure from the panel edge: 60 = 24 top padding + 20 title slot + 16 gap.
const IMAGE_TOP_MOBILE = '60px'
const IMAGE_ASPECT_MOBILE = '323 / 389'
// Image top + rendered image height + 16 gap, clamped (with the image maxHeight below) so short
// viewports keep the arrows and text on screen.
const ARROWS_TOP_MOBILE = 'min(calc((100vw - 32px) * 389 / 323 + 76px), calc(100dvh - 174px))'
const IMAGE_MAX_HEIGHT_MOBILE = 'calc(100dvh - 250px)'

const panelCss = css.raw({
  backgroundColor: 'white',
  width: { base: '100vw', lg: PANEL_WIDTH_LG },
  maxWidth: '100vw',
  height: { base: '100dvh', lg: PANEL_HEIGHT_LG },
  maxHeight: { base: '100dvh', lg: '90vh' },
  overflow: 'hidden',
})

// Zero the modal recipe's padding on mobile — the full-screen panel owns it.
const modalContentCss = css.raw({
  paddingX: { base: '0', lg: 'modal-padding-horizontal' },
  paddingY: { base: '0', lg: 'modal-padding-vertical' },
})

const carouselCss = css.raw({ width: 'full', height: 'full' })
const carouselFillCss = css.raw({ height: 'full' })

const slideCss = css({
  display: 'flex',
  flexDirection: { base: 'column', lg: 'row' },
  width: 'full',
  height: 'full',
})

const imageBoxCss = css({
  position: 'relative',
  flexShrink: 0,
  width: { base: 'auto', lg: IMAGE_WIDTH_LG },
  height: { base: 'auto', lg: 'full' },
  maxHeight: { base: IMAGE_MAX_HEIGHT_MOBILE, lg: 'none' },
  aspectRatio: { base: IMAGE_ASPECT_MOBILE, lg: 'auto' },
  marginTop: { base: IMAGE_TOP_MOBILE, lg: '0' },
  marginX: { base: 'space-16', lg: '0' },
  backgroundColor: { base: 'white', lg: 'black' },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
})

const imageCss = css({ width: 'full', height: 'full', objectFit: { base: 'cover', lg: 'contain' } })

// Mobile top padding: 68 = 16 gap to image + 40 arrow row + 12 gap to the meta row.
const contentBoxCss = css({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
  gap: { base: 'space-12', lg: 'space-16' },
  paddingTop: { base: '68px', lg: 'space-48' },
  paddingX: { base: 'space-16', lg: 'space-48' },
  paddingBottom: { base: 'space-24', lg: 'space-48' },
})

const metaRowCss = css({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: { base: 'space-8', lg: 'space-16' },
})
const authorCss = css({ textStyle: { base: 'body-14', lg: 'title-12' }, color: 'black' })
const titleCss = css({ textStyle: { base: 'title-14', lg: 'title-24-dec' }, color: 'black' })
// +4 margin so mobile title↔body spacing reads 16 while the container gap is 12.
const bodyCss = css({
  textStyle: { base: 'body-14', lg: 'body-16' },
  color: 'black',
  wordBreak: 'break-word',
  marginTop: { base: 'space-4', lg: '0' },
})
// No CTA on mobile by design.
const shopNowCss = css.raw({ display: { base: 'none', lg: 'flex' }, width: 'full', marginTop: 'space-16' })

// Mobile: under the image, inset from the right edge. Desktop: flush with the panel's
// bottom-right corner, against the modal's natural padding.
const arrowsRowCss = css({
  position: 'absolute',
  zIndex: 2,
  display: 'flex',
  gap: 'space-4',
  top: { base: ARROWS_TOP_MOBILE, lg: 'auto' },
  bottom: { base: 'auto', lg: '0' },
  right: { base: 'space-16', lg: '0' },
})

const SafeHtml = ({ html, className }: { html: string; className?: string }) => (
  // eslint-disable-next-line react/no-danger
  <div className={className} dangerouslySetInnerHTML={{ __html: getSanitizedHtml(html) }} />
)

function CarouselArrows() {
  const { t } = useTranslation('common')
  const { canScrollPrev, canScrollNext } = useCarousel()

  if (!canScrollPrev && !canScrollNext) {
    return null
  }

  return (
    <div className={arrowsRowCss}>
      <CarouselPreviousButton aria-label={t('previous')} />
      <CarouselNextButton aria-label={t('next')} />
    </div>
  )
}

// React 18 has no `inert` prop type; the bare attribute takes inactive slides (and their
// Shop Now buttons) out of the tab order and accessibility tree.
const inertProps = { inert: '' } as unknown as HTMLAttributes<HTMLDivElement>

function PopupSlide({ index, children }: { index: number; children: ReactNode }) {
  const { selectedIndex } = useCarousel()
  return (
    <CarouselItem css={carouselFillCss} {...(index === selectedIndex ? {} : inertProps)}>
      {children}
    </CarouselItem>
  )
}

// Latch: fires onNearEnd once per approach, resets when the user moves back away from the end.
function CarouselPaginationWatcher({ hasMore, onNearEnd }: { hasMore: boolean; onNearEnd: () => void }) {
  const { selectedIndex, slideCount } = useCarousel()
  const hasTriggeredNearEndRef = useRef(false)

  useEffect(() => {
    const isNearEnd = slideCount > 0 && selectedIndex >= slideCount - NEAR_END_THRESHOLD
    if (!isNearEnd) {
      hasTriggeredNearEndRef.current = false
      return
    }
    if (hasMore && !hasTriggeredNearEndRef.current) {
      hasTriggeredNearEndRef.current = true
      onNearEnd()
    }
  }, [selectedIndex, slideCount, hasMore, onNearEnd])

  return null
}

export function ReviewPopupViewDS({
  showPopUp,
  productReviews,
  reviewId,
  imageIndex,
  onClose,
  testId,
  hasMore = false,
  onNearEnd,
}: ReviewPopupViewDSProps) {
  const { t } = useTranslation('common')
  const productTitle = pdpStateManager.getProductTitleFromRedux()

  const { slideReviews, initialPosition } = useMemo(() => {
    if (!showPopUp) {
      return { slideReviews: [] as SlideReview[], initialPosition: -1 }
    }
    try {
      const slides: SlideReview[] = []
      let position = -1
      let count = 0
      productReviews.forEach(productReview => {
        productReview.imagesFileUrls?.forEach((imageUrl, i) => {
          slides.push({
            imageUrl,
            author: productReview.author,
            rating: productReview.rating,
            createdAt: productReview.createdAt,
            title: productReview.title,
            body: productReview.body,
          })
          if (productReview.id === reviewId && i === imageIndex) {
            position = count
          }
          count += 1
        })
      })
      return { slideReviews: slides, initialPosition: position }
    } catch (err) {
      captureException(err, {
        module: MODULES.REVIEW,
        feature: FEATURES.REVIEW_IMAGE_POPUP,
        extraInfo: { originalError: JSON.stringify(err) },
      })
      return { slideReviews: [] as SlideReview[], initialPosition: -1 }
    }
  }, [showPopUp, productReviews, reviewId, imageIndex])

  const carouselOpts = useMemo(() => ({ startIndex: initialPosition, align: 'center' as const }), [initialPosition])
  // Snap mode (no dragFree): a trackpad swipe pages one full-panel slide at a time. Vertical
  // wheel scrolling still reaches the review text's own overflowY container.
  const carouselPlugins = useMemo(() => [WheelGesturesPlugin()], [])

  const handleShopNow = () => {
    onClose()
    setTimeout(() => {
      // @i18n-ignore
      window.scroll({ top: 0, left: 0, behavior: 'smooth' })
    }, 0)
  }

  return (
    <ModalDS
      open={showPopUp && initialPosition !== -1}
      onClose={onClose}
      showCloseButton
      aria-label={t('reviews')}
      className={css(panelCss)}
      contentCss={modalContentCss}
    >
      <Carousel data-testid={testId} opts={carouselOpts} plugins={carouselPlugins} css={carouselCss}>
        {/* Arrows precede the slides so keyboard focus reaches them right after the close button. */}
        <CarouselArrows />
        <CarouselContent css={carouselFillCss} viewportCss={carouselFillCss}>
          {slideReviews.map((slide, index) => {
            const starValue = toStarRatingValue(slide.rating)
            return (
              <PopupSlide key={`${slide.author}_${slide.createdAt}_${index}`} index={index}>
                <div className={slideCss}>
                  <div className={imageBoxCss}>
                    <Image
                      url={slide.imageUrl}
                      title=""
                      alt={getReviewImageAltText({ reviewTitle: slide.title, productTitle })}
                      className={imageCss}
                      fillWithBlackBackground={false}
                      loading="lazy"
                    />
                  </div>
                  <div className={contentBoxCss}>
                    <div className={metaRowCss}>
                      <StarRatingBar
                        value={starValue}
                        aria-label={t('out_of_5_stars', { rating: parseFloat(starValue) })}
                      />
                      <div className={authorCss}>
                        {slide.author}{' '}
                        <DateTime
                          date={new Date(slide.createdAt)}
                          formatName="NUMERIC_SLASH_DATE"
                          overrides={STANDARD_DATE_OVERRIDES.REVIEW_ITEM}
                        />
                      </div>
                    </div>
                    <SafeHtml html={slide.title} className={titleCss} />
                    <SafeHtml html={slide.body} className={bodyCss} />
                    <Button variant="secondary" size="lg" onClick={handleShopNow} css={shopNowCss}>
                      {t('shop_now')}
                    </Button>
                  </div>
                </div>
              </PopupSlide>
            )
          })}
        </CarouselContent>
        {onNearEnd && <CarouselPaginationWatcher hasMore={hasMore} onNearEnd={onNearEnd} />}
      </Carousel>
    </ModalDS>
  )
}
